export interface PackPrefetchProgress {
  bytes: number;
  received: number;
  state: 'queued' | 'active' | 'settled';
  failed: boolean;
  claimed: boolean;
  consumed: boolean;
}
export interface ResourcePackPrefetch {
  version: 1;
  identity: string;
  downloads: Map<string, Promise<Uint8Array<ArrayBuffer>>>;
  records: Map<string, PackPrefetchProgress>;
  readResponse(response: Response, bytes: number, onProgress?: (bytes: number) => void, signal?: AbortSignal): Promise<Uint8Array<ArrayBuffer>>;
  subscribe(listener: () => void): () => void;
  consume(url: string, bytes: number): Promise<Uint8Array<ArrayBuffer>> | undefined;
  retry(url: string, bytes: number): Promise<Uint8Array<ArrayBuffer>> | undefined;
  release(url: string): void;
  dispose(): void;
}
type PrefetchGlobal = typeof globalThis & { __DDT_PACK_PREFETCH_V1__?: ResourcePackPrefetch };

/** Self-contained: Vite embeds this exact function in the document head. */
export function installResourcePackPrefetch(specs: ReadonlyArray<readonly [string, number]>, target: PrefetchGlobal = globalThis): ResourcePackPrefetch {
  const identity = JSON.stringify(specs);
  if (new Set(specs.map(([url]) => url)).size !== specs.length || specs.some(([url, bytes]) =>
    !url.startsWith('/game/packs/') || !Number.isSafeInteger(bytes) || bytes < 16)) throw Error('Invalid resource prefetch inventory');
  const previous = target.__DDT_PACK_PREFETCH_V1__;
  if (previous?.version === 1 && previous.identity === identity) return previous;
  previous?.dispose();
  const downloads = new Map<string, Promise<Uint8Array<ArrayBuffer>>>();
  const records = new Map<string, PackPrefetchProgress>();
  const listeners = new Set<() => void>();
  const controllers = new Set<AbortController>();
  const queue: Array<() => void> = [];
  let active = 0, disposed = false;
  const notify = () => { for (const listener of listeners) { try { listener(); } catch { /* UI must not interrupt transport. */ } } };
  const drain = () => { while (!disposed && active < 6 && queue.length) queue.shift()!(); };
  const readResponse = async (response: Response, size: number, progress?: (bytes: number) => void, signal?: AbortSignal): Promise<Uint8Array<ArrayBuffer>> => {
    if (signal?.aborted) throw signal.reason;
    if (!response.ok) throw Error(`Resource pack ${response.status}`);
    const body = response.body ?? new ReadableStream<Uint8Array>({
      async start(stream) { try { stream.enqueue(new Uint8Array(await response.arrayBuffer())); stream.close(); } catch (error) { stream.error(error); } },
    });
    const source = body.getReader();
    let reader: ReadableStreamDefaultReader<Uint8Array> = source;
    const cancel = (reason?: unknown) => {
      void reader.cancel(reason).catch(() => {});
      if (reader !== source) void source.cancel(reason).catch(() => {});
    };
    const abort = () => cancel(signal?.reason);
    signal?.addEventListener('abort', abort, { once: true });
    try {
      // HTTP may decode an outer edge layer and leave the stored gzip envelope.
      // Sniff the body itself, including magic split across one-byte chunks.
      const first: Uint8Array[] = [], magic = new Uint8Array(3); let count = 0;
      while (count < 3) {
        const item = await source.read();
        if (signal?.aborted) throw signal.reason;
        if (item.done) break;
        first.push(item.value);
        const length = Math.min(item.value.length, 3 - count);
        magic.set(item.value.subarray(0, length), count); count += length;
      }
      let cursor = 0;
      const replay = new ReadableStream<Uint8Array>({
        async pull(stream) {
          if (cursor < first.length) { stream.enqueue(first[cursor++]); return; }
          const item = await source.read();
          if (item.done) stream.close(); else stream.enqueue(item.value);
        },
        cancel(reason) { return source.cancel(reason); },
      });
      if (count === 3 && magic[0] === 31 && magic[1] === 139 && magic[2] === 8) {
        if (typeof DecompressionStream !== 'function') throw Error('Native gzip decompression unavailable');
        // DOM and worker declarations disagree on writable BufferSource variance;
        // both native APIs accept the Uint8Array chunks replay emits.
        const gzip = new DecompressionStream('gzip') as unknown as ReadableWritablePair<Uint8Array, Uint8Array>;
        reader = replay.pipeThrough(gzip).getReader();
      } else reader = replay.getReader();
      const bytes = new Uint8Array(size); let length = 0;
      while (true) {
        const item = await reader.read();
        if (signal?.aborted) throw signal.reason;
        if (item.done) break;
        if (item.value.byteLength > size - length) throw Error('Oversized resource pack');
        bytes.set(item.value, length); length += item.value.byteLength; progress?.(length);
      }
      if (length !== size) throw Error('Truncated resource pack');
      return bytes;
    } catch (error) { cancel(error); throw error; }
    finally { signal?.removeEventListener('abort', abort); }
  };
  const prefetch: ResourcePackPrefetch = {
    version: 1, identity, downloads, records, readResponse,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    consume(url, bytes) {
      const record = records.get(url);
      if (!record || record.bytes !== bytes || record.claimed || record.consumed || disposed) return;
      record.claimed = true;
      return downloads.get(url);
    },
    retry(url, bytes) {
      const record = records.get(url);
      if (!record || record.bytes !== bytes || !record.consumed || downloads.has(url) || disposed) return;
      const promise = enqueue(url, bytes);
      records.get(url)!.claimed = true;
      return promise;
    },
    release(url) {
      downloads.delete(url);
      const record = records.get(url); if (record) record.consumed = true;
      notify();
    },
    dispose() {
      disposed = true;
      for (const controller of controllers) controller.abort(Error('Resource prefetch replaced'));
      // Start queued jobs only to reject their already-observed promises.
      while (queue.length) queue.shift()!();
      downloads.clear(); listeners.clear();
    },
  };
  target.__DDT_PACK_PREFETCH_V1__ = prefetch;
  const enqueue = (url: string, size: number): Promise<Uint8Array<ArrayBuffer>> => {
    const record: PackPrefetchProgress = { bytes: size, received: 0, state: 'queued', failed: false, claimed: false, consumed: false };
    records.set(url, record);
    let resolve!: (bytes: Uint8Array<ArrayBuffer>) => void, reject!: (error: unknown) => void;
    const promise = new Promise<Uint8Array<ArrayBuffer>>((done, fail) => { resolve = done; reject = fail; });
    // Rejections remain stored for the loader's first consumption, without
    // reporting an unhandled rejection before the module has even arrived.
    void promise.catch(() => {});
    downloads.set(url, promise);
    queue.push(() => {
      if (disposed) { record.failed = true; record.state = 'settled'; reject(Error('Resource prefetch replaced')); return; }
      active++; record.state = 'active'; notify();
      const controller = new AbortController(); controllers.add(controller);
      let timeout!: ReturnType<typeof setTimeout>;
      const expiry = new Promise<never>((_, fail) => {
        timeout = setTimeout(() => {
          const error = Error('Resource pack download timeout');
          controller.abort(error); fail(error);
        }, 60_000);
      });
      const interrupted = new Promise<never>((_, fail) => controller.signal.addEventListener('abort', () => {
        fail(controller.signal.reason);
      }, { once: true }));
      const download = async () => {
        const response = await fetch(url, { signal: controller.signal });
        return readResponse(response, size, length => { record.received = length; notify(); }, controller.signal);
      };
      void Promise.race([download(), expiry, interrupted]).then(resolve, error => {
        record.failed = true; record.received = 0; reject(error);
      }).finally(() => {
        clearTimeout(timeout); controllers.delete(controller); active--;
        record.state = 'settled'; notify(); drain();
      });
    });
    drain();
    return promise;
  };
  for (const [url, size] of specs) enqueue(url, size);
  return prefetch;
}

// The serialized head factory and the fallback loader use this same decoder.
// A private empty factory neither starts a download nor changes the page global.
let standaloneReader: ResourcePackPrefetch['readResponse'] | undefined;
export function readResourcePackResponse(response: Response, bytes: number, onProgress?: (bytes: number) => void, signal?: AbortSignal): Promise<Uint8Array<ArrayBuffer>> {
  standaloneReader ??= installResourcePackPrefetch([], {} as PrefetchGlobal).readResponse;
  return standaloneReader(response, bytes, onProgress, signal);
}

/** An HTML prefetch from a different deployment must never seed this loader. */
export function resourcePackPrefetchFor(packs: ReadonlyArray<{ url: string; bytes: number }>): ResourcePackPrefetch | undefined {
  const target = globalThis as PrefetchGlobal, prefetch = target.__DDT_PACK_PREFETCH_V1__;
  if (!prefetch) return;
  if (prefetch.version === 1 && prefetch.identity === JSON.stringify(packs.map(pack => [pack.url, pack.bytes]))) return prefetch;
  prefetch.dispose(); delete target.__DDT_PACK_PREFETCH_V1__;
}
